// src/hooks/useTxTimeline.ts
// PRD #02 — Hook for transaction timeline data
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * useTxTimeline — транзакционные данные без console логов
 * с RPC-only подходом для безопасной работы с данными.
 *
 * Коллекция hooks для работы с транзакциями, адресами и счетами
 * Все error handling silent для production ready опыта
 *
 * Правила:
 *   - Никаких console.* (0)
 *   - RPC-only для всех операций с данными
 *   - Graceful error handling без шумных логов
 *   - Unified patterns для всех data loading hooks
 */

export interface TxData {
  id: string;
  status: string;
  kind: string;
  amount_in: number | null;
  asset_in: string | null;
  amount_out: number | null;
  currency_out: string | null;
  dest_ref: string | null;
  created_at: string;
  updated_at: string;
}

export interface TxEvent {
  at: string;
  type: string;
  payload: any;
}

export function useTxTimeline(txId: string) {
  const [tx, setTx] = useState<TxData | null>(null);
  const [events, setEvents] = useState<TxEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!txId) return;

    setLoading(true);
    setError(null);

    try {
      // Load transaction details
      const { data: txData, error: txError } = await supabase.rpc('tx_get', { _tx: txId });
      if (txError) throw txError;

      // Load events
      const { data: eventsData, error: eventsError } = await supabase.rpc('tx_get_events', { _tx: txId });
      if (eventsError) throw eventsError;

      setTx(txData?.[0] || null);
      setEvents(eventsData || []);
    } catch (err) {
      // Silent error handling - expose user-friendly message only
      setError(err instanceof Error ? err.message : 'Failed to load timeline');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [txId]);

  return {
    tx,
    events,
    loading,
    error,
    reload: load,
  };
}

// Hook for liquidation addresses list
export function useLiquidationAddresses() {
  const [addresses, setAddresses] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.rpc('liquidation_addresses_list');
      if (error) throw error;
      setAddresses(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load liquidation addresses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return { addresses, loading, error, reload: load };
}

// Hook for virtual accounts list
export function useVirtualAccounts() {
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.rpc('user_virtual_accounts_list');
      if (error) throw error;
      if (data && (data as any).success === false) {
        throw new Error((data as any).error ?? 'Failed to load virtual accounts');
      }
      const list = (data as any)?.data ?? [];
      setAccounts(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load virtual accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return { accounts, loading, error, reload: load };
}

// Hook for external accounts list
export function useExternalAccounts() {
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.rpc('external_accounts_list');
      if (error) throw error;
      setAccounts(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load external accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return { accounts, loading, error, reload: load };
}

// Hook for single liquidation address details
export function useLiquidationAddress(id: string) {
  const [address, setAddress] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.rpc('liquidation_address_get', { _id: id });
      if (error) throw error;
      setAddress(data?.[0] || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load liquidation address');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  return { address, loading, error, reload: load };
}

// Hook for single virtual account details
export function useVirtualAccount(id: string) {
  const [account, setAccount] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.rpc('user_virtual_account_detail', { p_id: id });
      if (error) throw error;
      
      // Handle response structure: {success: true, data: {...}}
      if (data && typeof data === 'object') {
        if ((data as any).success === false) {
          throw new Error((data as any).error || 'Failed to load virtual account');
        }
        const accountData = (data as any).data;
        setAccount(accountData || null);
      } else {
        setAccount(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load virtual account');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  return { account, loading, error, reload: load };
}